/**
 * Таймлайн проекта: этапы полосами, датированные элементы точками.
 *
 * Один компонент для обзора и для страницы этапов — данные и поведение
 * одинаковы, отличается только ширина. Дублировать таймлайн значило бы
 * поддерживать две расходящиеся реализации одного и того же.
 *
 * Что показывается:
 *   - полосы этапов, текущий выделен;
 *   - готовность требований прямо в полосе;
 *   - точки сроков: выполненные, просроченные, блокеры;
 *   - линия «сегодня».
 *
 * Что можно делать:
 *   - щелчок по этапу или точке открывает карточку;
 *   - точку можно перетащить на другую дату — это меняет срок элемента;
 *   - наведение показывает подробности.
 *
 * Начало этапа в данных не хранится: у этапа есть только целевая дата.
 * Полоса ведётся от конца предыдущего этапа, а первая — от даты создания
 * проекта. Допущение подписано под шкалой, чтобы полосу не приняли за
 * плановый график.
 */

import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { AlertOctagonIcon, CheckIcon, FlagIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { TimelineDto, TimelineItemDto, TimelineStageDto } from '@project-os/contracts';
import { isOverdue } from '@project-os/contracts';
import { ApiError } from '@/api/client';
import { useUpdateItem } from '@/api/items';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { calendarDaysFromToday, dayKey, formatDate, pluralRu } from '@/lib/format';
import { memberColorStyle } from '@/lib/member-color';
import { cn } from '@/lib/utils';
import { ItemTypeIcon } from '../items/item-icon';

const DAY_MS = 86_400_000;

/** Минимальная ширина шкалы, чтобы месяцы не слипались. */
const MIN_SPAN_DAYS = 30;

const STAGE_BAR_CLASS: Record<string, string> = {
  UPCOMING: 'bg-muted-foreground/20',
  CURRENT: 'bg-primary',
  COMPLETED: 'bg-tone-success/60',
  SKIPPED: 'bg-muted-foreground/12',
};

function toTime(day: string): number {
  return Date.parse(`${day}T00:00:00Z`);
}

function monthLabel(time: number): string {
  return new Intl.DateTimeFormat('ru-RU', { month: 'short', year: '2-digit' }).format(
    new Date(time),
  );
}

export function ProjectTimeline({
  projectId,
  data,
  canEdit,
  onOpenItem,
  onOpenStage,
  height = 'normal',
}: {
  projectId: string;
  data: TimelineDto;
  canEdit: boolean;
  onOpenItem: (itemId: string) => void;
  /** Переход к этапу. На странице этапов — прокрутка к карточке. */
  onOpenStage?: (stageId: string) => void;
  /** compact — для обзора, normal — для страницы этапов. */
  height?: 'compact' | 'normal';
}) {
  const update = useUpdateItem(projectId);
  const [dragging, setDragging] = useState<TimelineItemDto | null>(null);
  const [dropDay, setDropDay] = useState<string | null>(null);

  const sensors = useSensors(
    // Перетаскивание начинается после смещения: иначе щелчок по точке
    // превращался бы в перенос срока.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const model = useMemo(() => {
    const now = Date.now();
    const times = [
      toTime(data.projectStart),
      now,
      ...data.stages.flatMap((stage) => [toTime(stage.startDate), toTime(stage.endDate)]),
      ...data.items.map((item) => toTime(item.date)),
    ];

    let min = Math.min(...times);
    let max = Math.max(...times);

    // Поля по краям, чтобы крайние метки не упирались в границу.
    const padding = Math.max((max - min) * 0.03, DAY_MS * 2);
    min -= padding;
    max += padding;
    if (max - min < MIN_SPAN_DAYS * DAY_MS) max = min + MIN_SPAN_DAYS * DAY_MS;

    const total = max - min;
    const percent = (time: number) => ((time - min) / total) * 100;
    const timeAt = (ratio: number) => min + ratio * total;

    const ticks: { time: number; label: string }[] = [];
    const cursor = new Date(min);
    cursor.setDate(1);
    cursor.setHours(0, 0, 0, 0);
    while (cursor.getTime() <= max) {
      if (cursor.getTime() >= min) {
        ticks.push({ time: cursor.getTime(), label: monthLabel(cursor.getTime()) });
      }
      cursor.setMonth(cursor.getMonth() + 1);
    }

    return { min, max, percent, timeAt, ticks, now };
  }, [data]);

  const { percent, timeAt, ticks, now } = model;

  /**
   * Точки сроков раскладываются по рядам, чтобы близкие даты не наезжали
   * друг на друга: соседняя точка уходит на ряд ниже, если ближе 2 %.
   */
  const laidOut = useMemo(() => {
    const rows: number[][] = [];
    return data.items.map((item) => {
      const left = percent(toTime(item.date));
      let row = 0;
      while (rows[row]?.some((taken) => Math.abs(taken - left) < 2)) row += 1;
      (rows[row] ??= []).push(left);
      return { item, left, row };
    });
  }, [data.items, percent]);

  const rowCount = Math.max(1, ...laidOut.map((entry) => entry.row + 1));

  const handleDragEnd = async () => {
    const item = dragging;
    const day = dropDay;
    setDragging(null);
    setDropDay(null);

    if (!item || !day || day === item.date) return;

    try {
      await update.mutateAsync({ itemId: item.id, dueDate: day });
      toast.success(`Срок «${item.title}» — ${formatDate(day)}`);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось изменить срок');
    }
  };

  /** Дата под курсором во время переноса: считается из смещения по ширине. */
  const dayUnderPointer = (startDay: string, deltaX: number, width: number): string => {
    const ratio = percent(toTime(startDay)) / 100 + deltaX / width;
    const clamped = Math.min(Math.max(ratio, 0), 1);
    return dayKey(new Date(timeAt(clamped)));
  };

  const barHeight = height === 'compact' ? 'h-6' : 'h-8';

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToHorizontalAxis]}
      onDragStart={(event) =>
        setDragging((event.active.data.current?.item as TimelineItemDto) ?? null)
      }
      onDragMove={(event) => {
        // Смещение в пикселях переводится в дни через ширину дорожки:
        // dnd-kit не знает о нашей шкале и отдаёт только delta.
        const item = event.active.data.current?.item as TimelineItemDto | undefined;
        const width = trackWidth();
        if (item && width > 0) {
          setDropDay(dayUnderPointer(item.date, event.delta.x, width));
        }
      }}
      onDragEnd={() => void handleDragEnd()}
      onDragCancel={() => {
        setDragging(null);
        setDropDay(null);
      }}
    >
      <div className="relative" data-timeline-track>
        {/* Шкала месяцев */}
        <div className="relative h-4" aria-hidden>
          {ticks.map((tick) => (
            <span
              key={tick.time}
              className="absolute top-0 pl-1 text-2xs whitespace-nowrap text-muted-foreground"
              style={{ left: `${percent(tick.time)}%` }}
            >
              {tick.label}
            </span>
          ))}
        </div>

        {/* Сетка месяцев и линия «сегодня» */}
        <div className="pointer-events-none absolute inset-x-0 top-4 bottom-0" aria-hidden>
          {ticks.map((tick) => (
            <div
              key={tick.time}
              className="absolute inset-y-0 border-l border-border/40"
              style={{ left: `${percent(tick.time)}%` }}
            />
          ))}
          <div
            className="absolute inset-y-0 w-px bg-primary/60"
            style={{ left: `${percent(now)}%` }}
          />
        </div>

        {/* Полосы этапов */}
        {data.stages.length > 0 ? (
          <div className="relative space-y-1 pt-2">
            {data.stages.map((stage) => (
              <StageBar
                key={stage.id}
                stage={stage}
                left={percent(toTime(stage.startDate))}
                width={Math.max(
                  percent(toTime(stage.endDate)) - percent(toTime(stage.startDate)),
                  1.5,
                )}
                heightClass={barHeight}
                onOpen={onOpenStage}
              />
            ))}
          </div>
        ) : (
          <p className="relative pt-3 text-xs text-muted-foreground">
            Этапы появятся на шкале, когда у них будет целевая дата.
          </p>
        )}

        {/* Точки сроков */}
        {laidOut.length > 0 ? (
          <div
            className="relative mt-2"
            style={{ height: `${rowCount * (height === 'compact' ? 14 : 18) + 6}px` }}
          >
            {laidOut.map(({ item, left, row }) => (
              <DeadlineDot
                key={item.id}
                item={item}
                left={dragging?.id === item.id && dropDay ? percent(toTime(dropDay)) : left}
                top={row * (height === 'compact' ? 14 : 18)}
                canEdit={canEdit}
                onOpen={onOpenItem}
              />
            ))}
          </div>
        ) : null}
      </div>

      <p className="mt-2 text-2xs leading-relaxed text-muted-foreground/80">
        Начало этапа не хранится: полоса ведётся от конца предыдущего, а первая — от даты создания
        проекта. Точками отмечены сроки элементов
        {canEdit ? ' — точку можно перетащить на другую дату' : ''}. Цвет метки — цвет
        ответственного; красная рамка означает просрочку, квадрат — блокер, приглушённое —
        выполненное.
      </p>

      <DragOverlay dropAnimation={null}>
        {dragging ? (
          <div className="surface-card border border-border-strong px-2 py-1 text-2xs shadow-overlay">
            {dragging.title}
            {dropDay ? (
              <span className="ml-1.5 text-primary tabular-nums">{formatDate(dropDay)}</span>
            ) : null}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

/** Ширина дорожки таймлайна в пикселях: нужна для пересчёта смещения в дни. */
function trackWidth(): number {
  const element = document.querySelector('[data-timeline-track]');
  return element instanceof HTMLElement ? element.clientWidth : 0;
}

function StageBar({
  stage,
  left,
  width,
  heightClass,
  onOpen,
}: {
  stage: TimelineStageDto;
  left: number;
  width: number;
  heightClass: string;
  onOpen?: (stageId: string) => void;
}) {
  const { done, total, blocked } = stage.readiness;
  const ratio = total > 0 ? Math.min(done / total, 1) : 0;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => onOpen?.(stage.id)}
          className={cn('relative block w-full', heightClass)}
          aria-label={`${stage.name}, ${stage.statusLabelRu}`}
        >
          <span
            className={cn(
              'absolute inset-y-0 flex items-center gap-1.5 overflow-hidden rounded-md px-2',
              'transition-[filter,box-shadow] duration-120 ease-out-soft hover:brightness-115',
              STAGE_BAR_CLASS[stage.status] ?? 'bg-muted-foreground/20',
              stage.isCurrent && 'ring-2 ring-primary/50 ring-offset-1 ring-offset-transparent',
            )}
            style={{ left: `${left}%`, width: `${width}%` }}
          >
            {/* Доля выполненных требований залита прямо в полосе: готовность
                видна, не открывая этап. */}
            {ratio > 0 ? (
              <span
                className="absolute inset-y-0 left-0 bg-white/20 transition-[width] duration-500 ease-out-soft"
                style={{ width: `${ratio * 100}%` }}
                aria-hidden
              />
            ) : null}

            <span
              className={cn(
                'relative truncate text-2xs font-medium',
                stage.status === 'CURRENT' ? 'text-primary-foreground' : 'text-foreground/90',
              )}
            >
              {stage.name}
            </span>

            {blocked > 0 ? (
              <AlertOctagonIcon className="relative size-3 shrink-0 text-tone-danger" aria-hidden />
            ) : null}
          </span>
        </button>
      </TooltipTrigger>

      <TooltipContent className="max-w-64">
        <p className="flex items-center gap-1.5 font-medium">
          <FlagIcon className="size-3" />
          {stage.name}
        </p>
        <p className="text-2xs opacity-80">
          {stage.statusLabelRu} · до {formatDate(stage.endDate)}
        </p>
        <p className="text-2xs opacity-80">
          {total > 0
            ? `${done} из ${total} требований выполнено${
                stage.readiness.skipped > 0 ? `, пропущено ${stage.readiness.skipped}` : ''
              }`
            : 'Требования не заданы'}
        </p>
        {stage.blockedRequirements.length > 0 ? (
          <p className="mt-1 text-2xs text-tone-danger">
            Заблокировано: {stage.blockedRequirements.join(', ')}
          </p>
        ) : null}
      </TooltipContent>
    </Tooltip>
  );
}

function DeadlineDot({
  item,
  left,
  top,
  canEdit,
  onOpen,
}: {
  item: TimelineItemDto;
  left: number;
  top: number;
  canEdit: boolean;
  onOpen: (itemId: string) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: item.id,
    data: { item },
    disabled: !canEdit,
  });

  const overdue = !item.isDone && isOverdue(item.date);
  const daysLate = calendarDaysFromToday(item.date) ?? 0;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          ref={setNodeRef}
          type="button"
          {...attributes}
          {...listeners}
          onClick={() => onOpen(item.id)}
          className={cn(
            // timeline-dot держит увеличение и тень, чтобы попадание в цель
            // размером в десять пикселей подтверждалось видимым откликом —
            // и с мыши, и с клавиатуры.
            'timeline-dot absolute rounded-full',
            canEdit && 'cursor-grab touch-none select-none active:cursor-grabbing',
            isDragging && 'drag-source',
          )}
          style={{ left: `${left}%`, top: `${top}px` }}
          aria-label={`${item.title}, срок ${formatDate(item.date)}${overdue ? `, просрочено на ${pluralRu(daysLate, ['день', 'дня', 'дней'])}` : ''}`}
        >
          {/* Цвет метки — всегда человек, и только он.
              Раньше здесь цвет означал состояние, а в календаре — цвет
              ответственного: два разных смысла у одинаковых кружков на одном
              экране. Теперь состояние передаётся тем, что цветом не является:
              блокер — квадратом, просроченное — красной рамкой, выполненное —
              приглушением. */}
          <span
            className={cn(
              'block size-2.5 ring-2 ring-background',
              // Форма: блокер единственный, что нужно заметить, не наводя
              // курсор, — и он единственный не круглый.
              item.isBlocker ? 'rounded-[2px]' : 'rounded-full',
              item.owner ? 'member-color' : 'bg-muted-foreground/50',
              item.isDone && 'member-done opacity-60',
              // Рамка вместо заливки: срочность не отбирает цвет у человека.
              (overdue || item.isBlocker) && !item.isDone && 'ring-tone-danger',
            )}
            style={item.owner ? memberColorStyle(item.owner.id) : undefined}
          />
        </button>
      </TooltipTrigger>

      <TooltipContent className="max-w-64">
        <p className="flex items-center gap-1.5 font-medium">
          <ItemTypeIcon type={item.type} className="size-3" />
          {item.title}
        </p>
        <p className="text-2xs opacity-80">
          {item.typeLabelRu} · {item.statusLabelRu} · {formatDate(item.date)}
        </p>
        {item.owner ? <p className="text-2xs opacity-80">{item.owner.name}</p> : null}
        {overdue ? (
          <p className="text-2xs text-tone-danger">
            Просрочено на {pluralRu(daysLate, ['день', 'дня', 'дней'])}
          </p>
        ) : null}
        {item.isDone ? (
          <p className="flex items-center gap-1 text-2xs text-tone-success">
            <CheckIcon className="size-3" />
            выполнено
          </p>
        ) : null}
      </TooltipContent>
    </Tooltip>
  );
}
